import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid2 as Grid,
  Button,
  IconButton,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  MapOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { addUserAssignedAreas, deleteUserAssignedAreas } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

const UpdateUserForm = (props) => {
  const [selectedAreaType, setSelectedAreaType] = useState('');
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedLGAs, setSelectedLGAs] = useState([]);

  const [loading, setLoading] = useState(false);
  const [userAssignedAreas, setUserAssignedAreas] = useState(null);
  const [showAddMoreInput, setShowAddMoreInput] = useState(false);

  // States for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [initialZonePermit, setInitialZonePermit] = useState([]);
  const [initialStatePermit, setInitialStatePermit] = useState([]);
  const [initialLgaPermit, setInitialLgaPermit] = useState([]);

  useEffect(() => {
    if (props.userData) {
      getUserAssignedAreas();
    }
  }, [props.userData]);

  const getUserAssignedAreas = () => {
    setUserAssignedAreas(props.userData.userAreaPermissions);
    const { zonePermission, statePermission, lgaPermission } = props.userData.userAreaPermissions;
    setInitialStatePermit(statePermission || []);
    setInitialZonePermit(zonePermission || []);
    setInitialLgaPermit(lgaPermission || []);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (selectedStates.length === 0 && selectedZones.length === 0 && selectedLGAs.length === 0) {
      openNotification({ type: 'error', message: 'No changes have been made' });
      return;
    }

    setLoading(true);
    const payload = {
      zoneId: selectedZones.map((z) => z.zoneId),
      stateId: selectedStates.map((s) => s.stateId),
      lgaId: selectedLGAs.map((l) => l.lgaId),
    };

    try {
      const res = await addUserAssignedAreas(props.userId, payload);
      if (res !== null) {
        openNotification({ type: 'success', message: `Permissions updated successfully!` });
        props.reload(true);
        // Reset local form states
        setSelectedLGAs([]);
        setSelectedStates([]);
        setSelectedZones([]);
        setShowAddMoreInput(false);
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Update failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAreas = async () => {
    const { payload, areaName } = pendingDelete;
    setLoading(true);
    setDeleteConfirmOpen(false);

    try {
      const res = await deleteUserAssignedAreas(props.userId, payload);
      if (res !== null) {
        openNotification({ type: 'success', message: `${areaName} removed successfully` });
        props.reload(true);
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Could not delete areas' });
    } finally {
      setLoading(false);
      setPendingDelete(null);
    }
  };

  const openDeleteDialog = (payload, areaName) => {
    setPendingDelete({ payload, areaName });
    setDeleteConfirmOpen(true);
  };

  const renderBadge = (label, onRemove) => (
    <Chip
      label={label}
      onDelete={onRemove}
      deleteIcon={<DeleteIcon sx={{ fontSize: '16px !important' }} />}
      sx={{
        m: 0.5,
        fontWeight: 600,
        bgcolor: alpha('#d32f2f', 0.05),
        color: '#d32f2f',
        border: '1px solid',
        borderColor: alpha('#d32f2f', 0.2),
        '& .MuiChip-deleteIcon': { color: '#d32f2f', '&:hover': { color: '#b71c1c' } },
      }}
    />
  );

  return (
    <Box>
      {/* SECTION: Current Assigned Areas */}
      <Box sx={{ mb: 4 }}>
        <Stack spacing={3}>
          {/* ZONES */}
          {initialZonePermit.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                gutterBottom
                sx={{ display: 'block', mb: 1 }}
              >
                ASSIGNED ZONES
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {initialZonePermit.map((z) =>
                  renderBadge(z.zoneName, () =>
                    openDeleteDialog({ zoneId: [z.zoneId] }, z.zoneName)
                  )
                )}
              </Box>
            </Box>
          )}

          {/* STATES */}
          {initialStatePermit.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                gutterBottom
                sx={{ display: 'block', mb: 1 }}
              >
                ASSIGNED STATES
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {initialStatePermit.map((s) =>
                  renderBadge(s.stateName, () =>
                    openDeleteDialog({ stateId: [s.stateId] }, s.stateName)
                  )
                )}
              </Box>
            </Box>
          )}

          {/* LGAs */}
          {initialLgaPermit.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                gutterBottom
                sx={{ display: 'block', mb: 1 }}
              >
                ASSIGNED LOCAL GOVERNMENTS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {initialLgaPermit.map((l) =>
                  renderBadge(l.lgaName, () => openDeleteDialog({ lgaId: [l.lgaId] }, l.lgaName))
                )}
              </Box>
            </Box>
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* SECTION: Add New Areas */}
      {!showAddMoreInput ? (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setShowAddMoreInput(true)}
          sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}
        >
          Add More Areas
        </Button>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha('#f9fafb', 0.5),
            borderRadius: '12px',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Select Permission Scope
          </Typography>
          <form onSubmit={handleUpdateUser}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Area Type</InputLabel>
                  <Select
                    value={selectedAreaType}
                    label="Area Type"
                    onChange={(e) => setSelectedAreaType(e.target.value)}
                    sx={{ borderRadius: '8px', bgcolor: '#fff' }}
                  >
                    <MenuItem value="zone">Zone</MenuItem>
                    <MenuItem value="state">State</MenuItem>
                    <MenuItem value="lga">LGA</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                {selectedAreaType === 'zone' && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={props.zoneList}
                    getOptionLabel={(option) => option.zoneName}
                    value={selectedZones}
                    onChange={(_, val) => setSelectedZones(val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Zones"
                        placeholder="Choose..."
                        sx={{ bgcolor: '#fff' }}
                      />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                )}

                {selectedAreaType === 'state' && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={props.stateList}
                    getOptionLabel={(option) => option.stateName}
                    value={selectedStates}
                    onChange={(_, val) => setSelectedStates(val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select States"
                        placeholder="Choose..."
                        sx={{ bgcolor: '#fff' }}
                      />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                )}

                {selectedAreaType === 'lga' && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={props.lgaList}
                    getOptionLabel={(option) => option.lgaName}
                    value={selectedLGAs}
                    onChange={(_, val) => setSelectedLGAs(val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select LGAs"
                        placeholder="Choose..."
                        sx={{ bgcolor: '#fff' }}
                      />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                )}
              </Grid>

              <Grid size={12}>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} color="inherit" />}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => {
                      setShowAddMoreInput(false);
                      setSelectedAreaType('');
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlined color="error" /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove <strong>{pendingDelete?.areaName}</strong> from this
            user's permissions?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="inherit"
            sx={{ fontWeight: 700 }}
          >
            No, Keep it
          </Button>
          <Button
            onClick={handleDeleteAreas}
            variant="contained"
            color="error"
            sx={{ fontWeight: 700, borderRadius: '8px' }}
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateUserForm;
